import React, { useCallback, useMemo } from "react";
import { useStochasticInvoices } from "../../hooks/useStochasticInvoices";
import { useDownloadInvoicesCsv } from "../../hooks/useDownloadInvoicesCsv";
import { createInvoicesColumns } from "../InvoicesColumns/InvoicesColumns";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import InvoiceListFilters from "../InvoiceListFilters/InvoiceListFilters";
import { StochasticInvoice } from "@/api/fetchStochasticInvoices/types";
import DataTable from "@/components/Datatable/Datatable";

const InvoiceList: React.FC = () => {
  const {
    invoices,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    handlePaginationChange,
    handleFilterChange,
    handleSortingChange,
    filters,
  } = useStochasticInvoices();

  const {
    loading: csvLoading,
    downloadInvoicesCsv,
    error: csvError,
  } = useDownloadInvoicesCsv();

  const columns = useMemo(() => createInvoicesColumns(), []);

  const onFilterChange = useCallback(
    (searchTerm: string) => {
      handleFilterChange(searchTerm);
      handlePaginationChange({
        pageIndex: 0,
        pageSize: pagination.pageSize,
      });
    },
    [handleFilterChange, handlePaginationChange, pagination.pageSize],
  );

  const handleExportCsv = useCallback(() => {
    const requestParams = {
      sortBy: sorting.length > 0 ? sorting[0].id : "invoicedAt",
      sortOrder:
        (sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : "desc") as "asc" | "desc",
      searchTerm: filters.searchTerm,
    };
    downloadInvoicesCsv(requestParams);
  }, [sorting, filters, downloadInvoicesCsv]);

  return (
    <MainPageWrapper error={error || csvError} title="Invoices">
      <InvoiceListFilters
        filters={filters}
        isExporting={csvLoading}
        onExportCsv={handleExportCsv}
        onFilterChange={onFilterChange}
      />

      <div className="relative">
        <DataTable<StochasticInvoice>
          columns={columns}
          data={invoices}
          error={error}
          loading={loading}
          noDataMessage="No invoices found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.id}
          sorting={sorting}
          totalCount={totalCount}
        />
      </div>
    </MainPageWrapper>
  );
};

export default InvoiceList;